import { ReactElement, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { ModalSize, QUERIES, showError } from '../../../../../_metronic/helpers'
import { useModal } from '../../../../components'
import { useAuth } from '../../../auth'
import { useChamadaAtivaOpcional } from '../../atendimento_medicos/chamada-ativa/ChamadaAtivaProvider'
import { NotaVersaoUsuario } from '../core/_models'
import { adiarNotaVersao, getNotaVigente, marcarNotaVersaoLida } from '../core/_requests'
import {
  deveAbrirNotaVigente,
  ehRotaDeAtendimentoEmCurso,
  foiAdiadaNaSessao,
  marcarAdiadaNaSessao,
} from '../core/regrasNotaVigente'
import { NotaVersaoModalConteudo } from './NotaVersaoModalConteudo'

// Falha na vigente não pode atrapalhar o uso (inclui o 403 da troca de senha pendente): sem nota.
const buscarVigenteSemErro = () => getNotaVigente().catch(() => null)

// O aviso da nota no sino não tem push: quem já está logado só descobre a nota nova consultando de novo.
const INTERVALO_VIGENTE_MS = 5 * 60 * 1000

/**
 * Abre cada nota de versão vigente uma vez por carga do app, sem atropelar outro modal nem a tela
 * de atendimento em curso. Fechar sem "Entendi" (Ler depois, X, Esc, fundo, link) conta como adiar.
 * A vigente é consultada de novo ao entrar, ao voltar para a aba e a cada 5 minutos com ela visível.
 */
export const NotaVersaoDisparador = () => {
  const { currentUser } = useAuth()
  const idUsuario = currentUser?.id ?? undefined
  const { pathname } = useLocation()
  const chamadaAtiva = useChamadaAtivaOpcional()?.atendimentoId != null
  const emAtendimento = chamadaAtiva || ehRotaDeAtendimentoEmCurso(pathname)
  const { isOpen, modalContent, showModal, closeModal } = useModal()
  const queryClient = useQueryClient()

  const chaveVigente = [QUERIES.NOTAS_VERSAO_VIGENTE, idUsuario]
  const { data: nota } = useQuery(chaveVigente, buscarVigenteSemErro, {
    enabled: !!idUsuario,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: INTERVALO_VIGENTE_MS,
    retry: false,
  })

  const exibidaRef = useRef<number | null>(null)
  const decisaoTomadaRef = useRef(false)
  const conteudoRef = useRef<ReactElement | null>(null)
  const nossoModalAberto =
    isOpen && conteudoRef.current !== null && modalContent === conteudoRef.current

  useEffect(() => {
    if (!nota || !idUsuario || exibidaRef.current === nota.id || emAtendimento || isOpen) return
    if (!deveAbrirNotaVigente(nota, foiAdiadaNaSessao(idUsuario, nota.id))) return

    const chave = [QUERIES.NOTAS_VERSAO_VIGENTE, idUsuario]
    const atualizarSituacao = (situacao: NotaVersaoUsuario['situacao']) => {
      queryClient.setQueryData<NotaVersaoUsuario | null | undefined>(chave, (atual) =>
        atual && atual.id === nota.id && atual.situacao !== 'LIDA' ? { ...atual, situacao } : atual
      )
      queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_HISTORICO)
    }

    const entendi = () => {
      decisaoTomadaRef.current = true
      marcarNotaVersaoLida(nota.id)
        .then(() => {
          atualizarSituacao('LIDA')
          // O backend também marca como lida a notificação da nota: o sino precisa recarregar.
          queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST])
          queryClient.invalidateQueries([QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT])
        })
        .catch(showError)
    }

    const aoFechar = () => {
      conteudoRef.current = null
      if (decisaoTomadaRef.current) return
      // A marca vem antes da rede: mesmo com o adiar falhando, não reabre nesta sessão.
      marcarAdiadaNaSessao(idUsuario, nota.id)
      adiarNotaVersao(nota.id)
        .then(() => atualizarSituacao('ADIADA'))
        .catch(showError)
    }

    const conteudo = <NotaVersaoModalConteudo nota={nota} onEntendi={entendi} />
    exibidaRef.current = nota.id
    decisaoTomadaRef.current = false
    conteudoRef.current = conteudo
    showModal({
      title: nota.titulo,
      content: conteudo,
      size: ModalSize.Large,
      centered: true,
      onClose: aoFechar,
    })
  }, [nota, idUsuario, emAtendimento, isOpen, showModal, queryClient])

  // Entrou num atendimento com a nota aberta: fecha sem registrar adiamento e tenta de novo depois.
  useEffect(() => {
    if (!emAtendimento || !nossoModalAberto) return
    decisaoTomadaRef.current = true
    exibidaRef.current = null
    closeModal()
  }, [emAtendimento, nossoModalAberto, closeModal])

  return null
}
